import React, { useState, useEffect } from 'react';
import { postAPI, postAPIForm } from '../../../../app/api';
import editIcon from "../../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function HomepageHighlights() {
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        page: 'HOME',
        highlight_text: '',
        description: '',
        blockquote: '',
        back_img: null,
        front_img: null,
        is_published: 0,
        status: 1
    });

    // Fetch all highlights
    useEffect(() => {
        fetchHighlights();
    }, []);

    const fetchHighlights = async () => {
        try {
            setLoading(true);
            const response = await postAPI('homepage-highlights/list', {});
            if (response?.status === 1) {
                setHighlights(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching highlights:', error);
            Swal.fire('Error', 'Failed to fetch highlights', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value, files, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : (files ? files[0] : value)
        }));
    };

    // Add/Update highlight
    const handleAddHighlight = async (e) => {
        e.preventDefault();
        try {
            if (!formData.highlight_text || !formData.description) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            let submitData = {
                page: formData.page,
                highlight_text: formData.highlight_text,
                description: formData.description,
                blockquote: formData.blockquote,
                is_published: formData.is_published,
                status: formData.status
            };

            if (editingId) submitData.id = editingId;

            // Upload back image if exists
            if (formData.back_img && typeof formData.back_img !== 'string') {
                const backFormData = new FormData();
                backFormData.append('baseDir', 'homepage');
                backFormData.append('filename', 'back_img');
                backFormData.append('original_file', formData.back_img);

                const backResponse = await postAPIForm('file/uploadFile', backFormData);
                if (backResponse?.status === 1) {
                    submitData.back_img = backResponse?.filepath;
                }
            }

            // Upload front image if exists
            if (formData.front_img && typeof formData.front_img !== 'string') {
                const frontFormData = new FormData();
                frontFormData.append('baseDir', 'homepage');
                frontFormData.append('filename', 'front_img');
                frontFormData.append('original_file', formData.front_img);

                const frontResponse = await postAPIForm('file/uploadFile', frontFormData);
                if (frontResponse?.status === 1) {
                    submitData.front_img = frontResponse?.filepath;
                }
            }

            const response = await postAPI('homepage-highlights/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Highlight saved successfully', 'success');
                resetForm();
                fetchHighlights();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save highlight', 'error');
            }
        } catch (error) {
            console.error('Error saving highlight:', error);
            Swal.fire('Error', error.message || 'Failed to save highlight', 'error');
        }
    };

    // Delete highlight
    const handleDeleteHighlight = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this highlight?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('homepage-highlights/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Highlight deleted successfully', 'success');
                    fetchHighlights();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete highlight', 'error');
            }
        }
    };

    // Toggle publish
    const handleTogglePublish = async (id, isPublished) => {
        try {
            const endpoint = isPublished ? 'homepage-highlights/unpublish' : 'homepage-highlights/publish';
            const response = await postAPI(endpoint, { id });
            if (response?.status === 1) {
                Swal.fire('Success', `Highlight ${isPublished ? 'unpublished' : 'published'} successfully`, 'success');
                fetchHighlights();
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to update publish status', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            page: 'HOME',
            highlight_text: '',
            description: '',
            blockquote: '',
            back_img: null,
            front_img: null,
            is_published: 0,
            status: 1
        });
        setShowForm(false);
        setEditingId(null);
    };

    const handleToggleForm = () => {
        if (showForm) {
            setShowForm(false);
            resetForm();
        } else {
            setEditingId(null);
            setFormData({
                page: 'HOME',
                highlight_text: '',
                description: '',
                blockquote: '',
                back_img: null,
                front_img: null,
                is_published: 0,
                status: 1
            });
            setShowForm(true);
        }
    };

    const handleEditHighlight = (highlight) => {
        setFormData({
            page: highlight.page || 'HOME',
            highlight_text: highlight.highlight_text || '',
            description: highlight.description || '',
            blockquote: highlight.blockquote || '',
            back_img: highlight.back_img || null,
            front_img: highlight.front_img || null,
            is_published: highlight.is_published || 0,
            status: highlight.status || 1
        });
        setEditingId(highlight.id);
        setShowForm(true);
    };

    return (
        <>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <h3 className="mb-0">Homepage Highlights Management</h3>
                            <button
                                className="btn btn-primary"
                                onClick={handleToggleForm}
                            >
                                {showForm ? 'Cancel' : 'Add New Highlight'}
                            </button>
                        </div>
                    </div>
                </div>

                {showForm && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5 className="card-title">{editingId ? 'Edit Highlight' : 'Add New Highlight'}</h5>
                            <form onSubmit={handleAddHighlight}>
                                <div className="row">
                                    {/* <div className="col-md-6 mb-3">
                                            <label className="form-label">Page</label>
                                            <select
                                                className="form-control"
                                                name="page"
                                                value={formData.page}
                                                onChange={handleChange}
                                            >
                                                <option value="HOME">Home</option>
                                                <option value="ABOUT">About</option>
                                                <option value="SERVICES">Services</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div> */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Highlight Text *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="highlight_text"
                                            value={formData.highlight_text}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        {/* <label className="form-label">
                                                <input
                                                    type="checkbox"
                                                    name="is_published"
                                                    checked={formData.is_published === 1}
                                                    onChange={handleChange}
                                                />
                                                {' '}Publish
                                            </label> */}
                                        <label className="form-label">Publish *</label>
                                        <select
                                            className="form-control"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >
                                            <option value={1}>Publish</option>
                                            <option value={0}>Unpublish</option>
                                        </select>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            rows="3"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Blockquote</label>
                                        <textarea
                                            className="form-control"
                                            name="blockquote"
                                            value={formData.blockquote}
                                            onChange={handleChange}
                                            rows="2"
                                            placeholder="Optional quote or testimonial"
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Background Image</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="back_img"
                                            onChange={handleChange}
                                            accept="image/*"
                                        />
                                        {formData.back_img && typeof formData.back_img === 'string' && (
                                            <small className="text-muted">Current: {formData.back_img}</small>
                                        )}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Front Image</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="front_img"
                                            onChange={handleChange}
                                            accept="image/*"
                                        />
                                        {formData.front_img && typeof formData.front_img === 'string' && (
                                            <small className="text-muted">Current: {formData.front_img}</small>
                                        )}
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-success">
                                    {editingId ? 'Update' : 'Add'} Highlight
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover cus-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Text</th>
                                            <th>Description</th>
                                            <th>Page</th>
                                            <th>Status</th>
                                            <th>Published</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {highlights && highlights.length > 0 ? (
                                            highlights.map((highlight, index) => (
                                                <tr key={index}>
                                                    <td>{highlight.highlight_text}</td>
                                                    <td>{highlight.description?.substring(0, 30)}...</td>
                                                    <td>{highlight.page}</td>
                                                    <td>
                                                        <span className={`badge badge-${highlight.status === 1 ? 'success' : 'danger'}`}>
                                                            {highlight.status === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-${highlight.is_published === 1 ? 'info' : 'secondary'}`}>
                                                            {highlight.is_published === 1 ? 'Published' : 'Draft'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn me-2 py-0 my-0 mx-0"
                                                            onClick={() => handleTogglePublish(highlight.id, highlight.is_published)}
                                                            title={highlight.is_published ? 'Unpublish' : 'Publish'}
                                                        >
                                                            {highlight.is_published ? 'Unpublish' : 'Publish'}
                                                        </button>
                                                        <button
                                                            className="btn me-2 py-0 my-0 mx-0"
                                                            onClick={() => handleEditHighlight(highlight)}
                                                        >
                                                            <img src={editIcon} />
                                                        </button>
                                                        <button
                                                            className="btn"
                                                            onClick={() => handleDeleteHighlight(highlight.id)}
                                                        >
                                                            <img src={deleteIcon} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    No highlights found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
